import type {
  LoginRequest,
  LoginResponse,
  SocialLoginRequest,
} from "#shared/types/fetch";
import { useNuxtServerApi } from "@/composables/http/useNuxtServerAPI";
import useGetUserRole from "~/utils/auth/useGetUserRole";
import { useProfileStore } from "~~/stores/auth/profile";
import { OTPVerificationType, ResponseCode } from "#shared/enums";

let serverCheckPromise: Promise<void> | null = null;
let serverCheckResolve: (() => void) | null = null;

export function useAuth() {
  const user = useState<any>("auth-user", () => null);
  const status = useState<string>("auth-status", () => "idle");
  const ready = useState<boolean>("auth-ready", () => false);
  const error = useState<string | null>("auth-error", () => null);

  const serverChecked = useState<boolean>("auth-server-checked", () => false);

  const serverReady = computed(() => {
    if (import.meta.client || serverChecked.value) {
      return Promise.resolve();
    }

    if (!serverCheckPromise) {
      serverCheckPromise = new Promise<void>((resolve) => {
        serverCheckResolve = resolve;
      });
    }
    return serverCheckPromise;
  });

  const $nuxtApi = useNuxtServerApi();

  const loading = computed(() => status.value === "loading");
  const isAuthenticated = computed(() => status.value === "authenticated");

  async function completeAuthenticatedFlow(
    authenticatedUser: any,
    redirectPath?: string,
  ) {
    user.value = authenticatedUser || null;
    status.value = "authenticated";
    ready.value = true;

    const { isViewer } = useGetUserRole(user.value);
    if (!isViewer()) {
      await logout("/auth/not-allowed");
    } else {
      await navigateTo(redirectPath || "/profiles");
    }
  }

  async function login(credentials: LoginRequest, redirectPath?: string) {
    status.value = "loading";
    error.value = null;

    try {
      const response = await $nuxtApi.post<LoginResponse>(
        "/api/auth/login",
        credentials,
      );

      if (!statusOk(response.status)) {
        throw new Error(response.message || "Login failed.");
      }

      if (!response.data) {
        throw new Error("Login failed: empty response.");
      }
      switch (response.data.code) {
        case ResponseCode.VERIFICATION_REQUIRED: {
          user.value = response.data?.user ?? null;

          status.value = "unauthenticated";
          ready.value = true;

          // return navigateTo(
          //   `/otp-verification?identifier=${response.data?.user?.email}&type=${OTPVerificationType.ACCOUNT_VERIFICATION}`,
          // );

          return navigateTo({
            path: "/otp-verification",
            query: {
              identifier: response.data?.user?.email,
              type: OTPVerificationType.ACCOUNT_VERIFICATION,
            },
          });
        }

        case ResponseCode.TWO_FACTOR_REQUIRED: {
          user.value = response.data?.user ?? null;
          status.value = "unauthenticated";
          ready.value = true;

          return navigateTo("/auth/two-factor");
        }

        case ResponseCode.LOGIN_SUCCESS:
        default: {
          user.value = response.data?.user ?? null;
          status.value = "authenticated";
          ready.value = true;

          await completeAuthenticatedFlow(response.data?.user, redirectPath);
        }
      }
    } catch (err: any) {
      console.error("[Auth] Login error:", err);
      error.value = err.message || "An unexpected error occurred";
      status.value = "unauthenticated";
      ready.value = true;
    }
  }

  async function socialLogin(
    credentials: SocialLoginRequest,
    redirectPath?: string,
  ) {
    status.value = "loading";
    error.value = null;

    try {
      const response = await $nuxtApi.post<LoginResponse>(
        "/api/auth/social-login",
        credentials,
      );

      if (statusOk(response.status)) {
        await completeAuthenticatedFlow(response.data?.user, redirectPath);
      } else {
        error.value =
          (response.data as any)?.message ||
          "Social login failed. Please try again.";
        status.value = "unauthenticated";
        ready.value = true;
      }
    } catch (err: any) {
      console.error("[Auth] Social login error:", err);
      error.value = err.message || "An unexpected error occurred";
      status.value = "unauthenticated";
      ready.value = true;
      throw err;
    }
  }

  async function autoAuthenticateUser(
    payload: any,
    route: string,
    redirectPath?: string,
  ) {
    status.value = "loading";
    error.value = null;

    try {
      const response = await $nuxtApi.post<LoginResponse>(route, payload);

      if (statusOk(response.status) && response.data?.user) {
        await completeAuthenticatedFlow(response.data.user, redirectPath);
        return response;
      }

      error.value =
        (response.data as any)?.message ||
        "Authentication failed. Please try again.";
      status.value = "unauthenticated";
      ready.value = true;
      return response;
    } catch (err: any) {
      console.error("[Auth] Authentication error:", err);
      error.value = err.message || "An unexpected error occurred";
      status.value = "unauthenticated";
      ready.value = true;
      throw err;
    }
  }

  async function join(data: any, redirectPath?: string) {
    status.value = "loading";
    error.value = null;

    try {
      const response = await $nuxtApi.post<LoginResponse>(
        "/api/auth/join",
        data,
      );

      if (statusOk(response.status)) {
        user.value = response.data?.user || null;
        status.value = "authenticated";

        await navigateTo(redirectPath || "/profiles");
      } else {
        error.value = (response.data as any)?.message || "Registration failed";
        status.value = "unauthenticated";
      }
    } catch (err: any) {
      console.error("[Auth] Registration error:", err);
      error.value = err.message || "An unexpected error occurred";
      status.value = "unauthenticated";
    }
  }

  async function fetchUser({ refresh = false } = {}) {
    if (status.value === "loading") {
      return new Promise((resolve) => {
        const unwatch = watch(status, (newStatus) => {
          if (newStatus !== "loading") {
            unwatch();
            resolve({ data: user.value, error: error.value });
          }
        });
      });
    }

    const wasAuthenticated = status.value === "authenticated";
    status.value = "loading";
    error.value = null;

    const url = refresh ? "/api/auth/me?refresh=true" : "/api/auth/me";

    try {
      const response = await $nuxtApi.get(url);

      if (statusOk(response.status)) {
        const newUser = (response.data as any)?.user || null;

        user.value = newUser;
        status.value = newUser ? "authenticated" : "unauthenticated";
        ready.value = true;

        return { data: user.value, error: null };
      } else {
        user.value = null;
        status.value = "unauthenticated";
        error.value = (response.data as any)?.message || "Session expired";
        clearAuthState();
        ready.value = true;

        if (wasAuthenticated && import.meta.client) {
          await navigateTo("/", { replace: true });
        }

        return { data: null, error: error.value };
      }
    } catch (err: any) {
      console.error("[Auth] Fetch user error:", err);
      error.value = err.message || "An unexpected error occurred";
      status.value = "unauthenticated";
      ready.value = true;
      return { data: null, error: error.value };
    }
  }

  async function logout(destination: string = "/") {
    status.value = "loading";
    error.value = null;
    const { activeProfile, clearActiveProfile } = useProfileStore();

    try {
      const response = await $nuxtApi.post("/api/auth/logout");

      if (activeProfile) clearActiveProfile();

      if (statusOk(response.status)) {
        // Clean success — clear and navigate
        // clearAuthState();
        return await navigateTo(destination);
      } else {
        // Server returned a non-OK status.
        // The session may or may not have been destroyed — don't keep
        // the user in a limbo authenticated state on the client.
        // Clear locally and navigate, but signal the partial failure.
        console.error("[Auth] Logout returned non-OK status:", response.status);
        clearAuthState();
        if (activeProfile) clearActiveProfile();
        return await navigateTo(destination);
      }
    } catch (err: any) {
      // Network error or 500 from the Nuxt handler.
      // The external API call or session destroy may have already run.
      // Again — don't leave the client in a stuck authenticated state.
      console.error("[Auth] Logout error:", err);
      clearAuthState();
      if (activeProfile) clearActiveProfile();
      return await navigateTo(destination);
    }
  }

  function setServerChecked() {
    if (import.meta.server) {
      serverChecked.value = true;
      if (serverCheckResolve) {
        serverCheckResolve();
      }
    }
  }

  function clearAuthState() {
    user.value = null;
    error.value = null;
    status.value = "unauthenticated";
  }

  function setAuthReady(value: boolean) {
    ready.value = value;
  }

  return {
    user,
    error,
    loading,
    status,
    ready,
    isAuthenticated,
    serverReady,

    login,
    socialLogin,
    autoAuthenticateUser,
    join,
    fetchUser,
    logout,
    clearAuthState,
    setAuthReady,
    setServerChecked,
  };
}
